import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import { useToast } from '@/hooks/useToast';
import { getErrorMessage } from '@/services/apiClient';
import {
  addSupportTicketParticipants,
  buildSupportTicketParticipantOptions,
  listSupportTicketParticipantCandidates,
  removeSupportTicketParticipant,
  transferSupportTicketOwner,
  type SupportTicket,
  type SupportTicketPeoplePermissions,
} from '@/services/supportTickets.service';

const NO_PERMISSIONS: SupportTicketPeoplePermissions = {
  addPeople: false,
  removeOthers: false,
  makeOwner: false,
  claimOwnership: false,
};

type PendingAction =
  | { kind: 'owner'; userId: number; name: string; self: boolean }
  | { kind: 'leave'; userId: number };

/**
 * The people on a ticket — its owner and the colleagues added to it — and
 * the controls to add, remove and hand over. Which controls show comes only
 * from `ticket.viewerCan`; the server enforces the same rules.
 */
export function SupportTicketPeople({ ticket }: { ticket: SupportTicket }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const ticketId = ticket.supportTicketId;

  const owner = ticket.owner ?? {
    userId: ticket.requesterUserId,
    name: ticket.requesterName,
    email: ticket.requesterEmail,
    isActive: true,
  };
  const participants = ticket.participants ?? [];
  const can = ticket.viewerCan ?? NO_PERMISSIONS;
  const viewerUserId = ticket.viewerUserId ?? null;
  const viewerIsSupport = ticket.viewerRole === 'support';

  const [pickerOpened, setPickerOpened] = useState(false);
  const [staged, setStaged] = useState<string[]>([]);
  const [pending, setPending] = useState<PendingAction | null>(null);

  const candidatesQuery = useQuery({
    queryKey: ['support-ticket-participant-candidates', ticketId],
    queryFn: () => listSupportTicketParticipantCandidates({ supportTicketId: ticketId }),
    enabled: can.addPeople && pickerOpened,
    staleTime: 60_000,
  });

  const candidateOptions = useMemo(
    () => buildSupportTicketParticipantOptions(candidatesQuery.data ?? []),
    [candidatesQuery.data],
  );

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
    void queryClient.invalidateQueries({ queryKey: ['support-ticket-list'] });
    void queryClient.invalidateQueries({ queryKey: ['support-ticket-summary'] });
    void queryClient.invalidateQueries({ queryKey: ['support-ticket-participant-candidates', ticketId] });
  };

  const addMutation = useMutation({
    mutationFn: (userIds: number[]) => addSupportTicketParticipants(ticketId, userIds),
    onSuccess: (_ticket, userIds) => {
      toast.success(userIds.length === 1 ? 'Added to the ticket' : 'People added to the ticket');
      refresh();
    },
    onError: (error) => {
      toast.error('Unable to add people', getErrorMessage(error));
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: number) => removeSupportTicketParticipant(ticketId, userId),
    onSuccess: (result) => {
      setPending(null);
      if (result.viewerLeft) {
        toast.success('You left the ticket');
        void queryClient.invalidateQueries({ queryKey: ['support-ticket-list'] });
        void queryClient.invalidateQueries({ queryKey: ['support-ticket-summary'] });
        queryClient.removeQueries({ queryKey: ['support-ticket', ticketId] });
        navigate('/help');
        return;
      }
      toast.success('Removed from the ticket');
      refresh();
    },
    onError: (error) => {
      toast.error('Unable to remove', getErrorMessage(error));
    },
  });

  const ownerMutation = useMutation({
    mutationFn: (userId: number) => transferSupportTicketOwner(ticketId, userId),
    onSuccess: () => {
      setPending(null);
      toast.success('Owner changed');
      refresh();
    },
    onError: (error) => {
      toast.error('Unable to change the owner', getErrorMessage(error));
    },
  });

  // The picker stages a selection while it is open and adds it in one
  // request when it closes.
  const handlePickerOpenChange = (open: boolean) => {
    if (open) {
      setPickerOpened(true);
      return;
    }
    const userIds = staged.map(Number).filter((id) => id > 0);
    setStaged([]);
    if (userIds.length > 0) {
      addMutation.mutate(userIds);
    }
  };

  const confirmPending = () => {
    if (!pending) return;
    if (pending.kind === 'owner') {
      ownerMutation.mutate(pending.userId);
    } else {
      removeMutation.mutate(pending.userId);
    }
  };

  let confirmText = '';
  let confirmLabel = '';
  if (pending?.kind === 'owner') {
    confirmLabel = pending.self ? 'Make me the owner' : 'Make owner';
    confirmText = pending.self
      ? `You'll become the owner of this ticket. ${owner.name} stays on it.`
      : viewerIsSupport
        ? `${pending.name} will become the owner of this ticket. ${owner.name} stays on it.`
        : `${pending.name} will become the owner of this ticket. You'll stay on it.`;
  } else if (pending?.kind === 'leave') {
    confirmLabel = 'Leave ticket';
    confirmText = "You'll stop getting emails about this ticket and won't be able to open it unless someone adds you again.";
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
      <ul className="space-y-3">
        <li className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-slate-900">{owner.name}</span>
              <Badge variant="neutral">Owner</Badge>
              {!owner.isActive && <span className="text-xs text-slate-500">No longer active</span>}
            </div>
            {owner.email && <div className="break-all">{owner.email}</div>}
          </div>
        </li>

        {participants.map((person) => {
          const isSelf = viewerUserId !== null && person.userId === viewerUserId;
          const canRemove = can.removeOthers || isSelf;
          const canMakeOwner = can.makeOwner && person.isActive;
          const canClaim = isSelf && can.claimOwnership;

          return (
            <li key={person.userId} className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-900">{person.name}</span>
                  {!person.isActive && <span className="text-xs text-slate-500">No longer active</span>}
                </div>
                {person.email && <div className="break-all">{person.email}</div>}
                {person.addedByName && <div className="text-xs text-slate-500">Added by {person.addedByName}</div>}
              </div>
              <div className="flex flex-wrap items-center gap-1">
                {canMakeOwner && (
                  <Button
                    variant="ghost"
                    size="xs"
                    aria-label={`Make owner: ${person.name}`}
                    onClick={() => setPending({ kind: 'owner', userId: person.userId, name: person.name, self: false })}
                  >
                    Make owner
                  </Button>
                )}
                {canClaim && (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setPending({ kind: 'owner', userId: person.userId, name: person.name, self: true })}
                  >
                    Make me the owner
                  </Button>
                )}
                {canRemove &&
                  (isSelf ? (
                    <Button variant="ghost" size="xs" onClick={() => setPending({ kind: 'leave', userId: person.userId })}>
                      Leave ticket
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="xs"
                      aria-label={`Remove ${person.name}`}
                      loading={removeMutation.isPending && removeMutation.variables === person.userId}
                      onClick={() => removeMutation.mutate(person.userId)}
                    >
                      Remove
                    </Button>
                  ))}
              </div>
            </li>
          );
        })}
      </ul>

      {can.addPeople && (
        <div className="mt-3">
          <SearchableMultiSelect
            options={candidateOptions}
            selectedValues={staged}
            onSelectionChange={setStaged}
            onOpenChange={handlePickerOpenChange}
            placeholder={addMutation.isPending ? 'Adding…' : 'Add people…'}
            searchPlaceholder="Search by name or email…"
            emptyMessage={
              candidatesQuery.isLoading
                ? 'Loading…'
                : candidatesQuery.isError
                  ? "Couldn't load your colleagues. Close this and try again."
                  : 'No one else at your practice to add.'
            }
            disabled={addMutation.isPending}
          />
        </div>
      )}

      <Modal
        isOpen={pending !== null}
        onClose={() => setPending(null)}
        title={pending?.kind === 'leave' ? 'Leave this ticket?' : 'Change the owner?'}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button onClick={confirmPending} loading={ownerMutation.isPending || removeMutation.isPending}>
              {confirmLabel}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600">{confirmText}</p>
      </Modal>
    </div>
  );
}
